import { type Component, createMemo } from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import { SlidersHorizontal } from 'lucide-solid';
import { SectionRail, type RailGroup } from '~/shared/ui/SectionRail';
import { SETTINGS_MODULES, type SettingsModule, nodesForModule } from './settings-nav';
import { useAuth } from '~/shared/api/auth-context';
import { canOpenSettingsNode, canSeeScreen, menuEntry } from '~/shared/access/sections';

export const SettingsNavPanel: Component = () => {
  const location = useLocation();
  const { session } = useAuth();
  const permissions = (): readonly string[] => session()?.user.permissions ?? [];
  const whole = (): boolean => canSeeScreen('/settings', permissions());

  const railItem = (m: SettingsModule) => {
    const children = nodesForModule(m.key)
      .filter((n) => n.href !== m.route && canOpenSettingsNode(n, permissions()))
      .map((n) => ({
        href: n.href,
        label: n.title,
        unavailable: n.comingSoon ?? undefined,
      }));
    return { href: m.route, label: m.label, icon: m.icon, children };
  };
  const openItems = (modules: SettingsModule[]) =>
    whole()
      ? modules.map(railItem)
      : modules.flatMap((m) =>
          nodesForModule(m.key)
            .filter((n) => !n.comingSoon && canOpenSettingsNode(n, permissions()))
            .map((n) => ({ href: n.href, label: n.title, icon: m.icon })),
        );

  const groups = createMemo((): RailGroup[] => [
    { key: 'modules', items: openItems(SETTINGS_MODULES.filter((m) => !m.standalone)) },
    ...SETTINGS_MODULES.filter((m) => m.standalone).map((m) => ({
      key: m.key,
      accent: { solid: m.accent, soft: m.standalone?.soft, hairline: m.standalone?.hairline },
      items: openItems([m]),
    })).filter((g) => g.items.length > 0),
  ]);

  return (
    <aside class="ml-3 flex w-[236px] shrink-0 flex-col sticky top-4 h-[calc(100vh-2rem)] animate-[settingsPanelIn_var(--duration-base)_var(--ease-ios)]">
      <A
        href={menuEntry('/settings', permissions()) ?? '/settings'}
        end
        class="flex items-center gap-2.5 border-b px-1 pb-3 shrink-0"
        style={{ 'border-color': 'var(--color-border-hairline)' }}
      >
        <span
          class="inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-[var(--radius-sm)]"
          style={{
            background: 'color-mix(in srgb, var(--color-accent) 14%, transparent)',
            'box-shadow': 'inset 0 0 0 1px color-mix(in srgb, var(--color-accent) 32%, transparent)',
            color: 'var(--color-accent)',
          }}
        >
          <SlidersHorizontal class="h-3.5 w-3.5" />
        </span>
        <span
          class="text-[15px] font-semibold tracking-tight"
          style={{
            color:
              location.pathname === '/settings'
                ? 'var(--color-text-primary)'
                : 'var(--color-text-secondary)',
          }}
        >
          Настройки
        </span>
      </A>

      <div class="min-h-0 flex-1 overflow-y-auto pt-3">
        <SectionRail
          groups={groups()}
          ariaLabel="Разделы настроек"
          accent="var(--color-accent)"
          orientation="column"
        />
      </div>

      <style>{`
        @keyframes settingsPanelIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </aside>
  );
};
